/**
 * Uppy Dashboard + Transloadit upload hook (Uppy v3).
 *
 * Each file creates 1 separate Transloadit assembly (scio1 behavior).
 * In Uppy v3, `assemblyOptions` is called per-file. We inject a unique
 * `fileID` field per file so the dedupe logic creates separate assemblies.
 *
 * The caller provides an `onUploadSuccess` callback to handle save logic,
 * making this hook reusable across features (assets, playlists, etc.).
 *
 * Uppy is built lazily on each open() call — not on mount.
 *
 * scio1 source: imports/ui/services/index.js → openUploadModal(), processUploads()
 * scio2 reference: optisigns-portal-common → transloadit.service.ts
 */
import { useCallback, useRef, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import type { ApolloClient } from '@apollo/client';
import { GET_FILE_UPLOAD_OPTIONS } from '@scio3/graphql-client';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Uppy from '@uppy/core';
import type { UppyFile, Restrictions } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import Transloadit, { COMPANION_URL, COMPANION_ALLOWED_HOSTS } from '@uppy/transloadit';
import type { AssemblyOptions } from '@uppy/transloadit';
import Webcam from '@uppy/webcam';
import GoogleDrive from '@uppy/google-drive';
import Dropbox from '@uppy/dropbox';
import OneDrive from '@uppy/onedrive';
import en_US from '@uppy/locales/lib/en_US';
import de_DE from '@uppy/locales/lib/de_DE';
import fr_FR from '@uppy/locales/lib/fr_FR';
import es_ES from '@uppy/locales/lib/es_ES';
import it_IT from '@uppy/locales/lib/it_IT';
import nl_NL from '@uppy/locales/lib/nl_NL';
import pt_BR from '@uppy/locales/lib/pt_BR';
import sv_SE from '@uppy/locales/lib/sv_SE';
import { validateImageResolution, validateVideoBitrate } from '../utils/file-validation';
import { showLoadingOverlay } from '@/components/shared';
import { useAuthStore } from '@/stores/auth-store';
import { reportAppError } from '@/lib/telemetry/faro';
import { classifyUploadError, isUploadCancelled, isUserFileUploadError } from '@/lib/upload-errors';

// --- Types ---

export interface UploadFileMeta {
  name: string;
  type: string;
  size: number;
  extension: string;
}

export interface TransloaditUploadOptions {
  /** Called per file on successful assembly upload. Caller handles save logic. */
  onUploadSuccess: (fileMeta: UploadFileMeta, assemblyId: string, bucket?: string) => void;
  /** Called when all files in a batch are done. */
  onAllComplete?: () => void;
  /** Override for getUploadOptions query variables (e.g. replace asset flow). */
  uploadOptionsPayload?: Record<string, unknown>;
  /** Restrict to single file upload (for replace asset flow). */
  restrictToSingleFile?: boolean;
  /** Custom note text for the Dashboard (overrides default). */
  dashboardNote?: string;
  /** Called when the user closes the Dashboard modal without uploading. */
  onModalClosed?: () => void;
  /** Custom function to fetch upload options — overrides default GET_FILE_UPLOAD_OPTIONS query.
   *  Used by share link context to call public endpoint (getUploadOptionsForShareLink). */
  fetchUploadOptionsFn?: () => Promise<{
    params?: Record<string, unknown>;
    bucket?: string;
    restrictions?: { maxFileSize?: number };
    signature?: string;
  } | null>;
  /** Caller-supplied Uppy restrictions, merged over the backend-provided restrictions
   *  (caller keys win). Composes with `restrictToSingleFile`: an explicit
   *  `restrictions.maxNumberOfFiles` here wins over both `restrictToSingleFile` and the
   *  backend value. */
  restrictions?: Partial<Restrictions>;
  /** When set, mounts the Dashboard inline into this element (or CSS selector) instead of
   *  the body modal (`inline: true, target: inlineTarget`). Modal-only plumbing
   *  (openModal/closeModal) is skipped in this mode.
   *  IMPORTANT: Uppy is built lazily inside `open()` (see doc comment above) — the target
   *  element MUST already exist in the DOM by the time the caller invokes `open()`. */
  inlineTarget?: HTMLElement | string;
  /** Drop-only mode: render a bare drag-drop + "browse files" box with NO cloud-provider
   *  tabs (My Device/Camera/Dropbox/OneDrive). Matches the legacy Canva PortalPage
   *  (`providers = []`). Used by the manifest `file-upload` field to keep the inline
   *  dropzone simple. */
  dropOnly?: boolean;
}

// --- Constants ---

const UPPY_LOCALE_MAP: Record<string, typeof en_US> = {
  en: en_US,
  de: de_DE,
  fr: fr_FR,
  es: es_ES,
  it: it_IT,
  nl: nl_NL,
  pt: pt_BR,
  sv: sv_SE,
};

const EMPTY_PAYLOAD: Record<string, unknown> = {};

// --- Upload-options prefetch cache ---
// The default upload-options query (`{ payload: {} }`) is stable per session. Prefetching it
// when an upload UI is about to appear (e.g. the Canva wizard opening, before the user reaches
// the upload step) lets a later inline `open()` build Uppy with zero wait — no loading flash.
// `network-only` bypasses the Apollo cache, so we memoize the in-flight PROMISE here (short TTL,
// well under the signed-params expiry) and have `getUploadOptions` reuse it for the default payload.
const PREFETCH_TTL_MS = 60_000;
let defaultOptionsCache: { promise: Promise<Record<string, unknown>>; at: number } | null = null;

const isDefaultPayload = (payload: Record<string, unknown>): boolean =>
  !payload || Object.keys(payload).length === 0;

/** Fresh (within TTL) cached default-options promise, or null. Time is passed in so callers
 *  share one `Date.now()` read. */
const freshCachedOptions = (now: number): Promise<Record<string, unknown>> | null =>
  defaultOptionsCache && now - defaultOptionsCache.at < PREFETCH_TTL_MS
    ? defaultOptionsCache.promise
    : null;

/**
 * Warm the default (`{ payload: {} }`) upload-options query so a subsequent inline `open()`
 * builds Uppy with no network wait. Fire-and-forget; safe to call repeatedly (deduped within
 * the TTL). Trigger it when an upload UI is about to be reachable — e.g. `ConfigEngineDialog`
 * calls this on open when the manifest has a `file-upload` field.
 */
export function prefetchUploadOptions(client: ApolloClient): void {
  if (freshCachedOptions(Date.now())) return; // already warm
  const promise = client
    .query<{ getFileUploadOptions?: Record<string, unknown> }>({
      query: GET_FILE_UPLOAD_OPTIONS,
      variables: { payload: {} },
      fetchPolicy: 'network-only',
    })
    .then(({ data }) => {
      const opts = data?.getFileUploadOptions;
      if (!opts) throw new Error('Failed to get upload options');
      return opts;
    });
  // A failed prefetch must not poison the cache — drop it so the real `open()` retries cleanly.
  promise.catch(() => {
    if (defaultOptionsCache?.promise === promise) defaultOptionsCache = null;
  });
  defaultOptionsCache = { promise, at: Date.now() };
}

/** Test-only: clear the module-level prefetch cache between cases. */
export function __resetUploadOptionsPrefetch(): void {
  defaultOptionsCache = null;
}

// --- Transforge reachability (client-side fallback) ---
// The backend hands out Transforge upload options (opts.endpoint set) whenever the
// account is on Transforge, but only the BROWSER can tell whether the Transforge
// gateway is actually reachable from here — the server sits inside the cluster and
// never sees the client's CORS / network / DNS / whitelist reality. So before we
// build Uppy against a Transforge endpoint, probe it; if it's unreachable we
// re-request options with forceTransloadit so we never create an assembly against a
// dead gateway (which would sit stuck in UPLOADING). Fail-closed: any probe failure
// (timeout, network, CORS, non-2xx) routes the upload to Transloadit.
const TF_HEALTH_TTL_MS = 60_000;
const TF_HEALTH_TIMEOUT_MS = 2500;
const tfReachableCache = new Map<string, { ok: boolean; at: number }>();

async function isTransforgeReachable(endpoint: string): Promise<boolean> {
  const cached = tfReachableCache.get(endpoint);
  if (cached && Date.now() - cached.at < TF_HEALTH_TTL_MS) return cached.ok;
  let ok = false;
  try {
    const url = new URL('/health', endpoint).toString();
    // Simple GET, no credentials → no CORS preflight; /health returns ACAO:* .
    const res = await fetch(url, {
      method: 'GET',
      signal: AbortSignal.timeout(TF_HEALTH_TIMEOUT_MS),
    });
    ok = res.ok;
  } catch {
    ok = false;
  }
  tfReachableCache.set(endpoint, { ok, at: Date.now() });
  return ok;
}

// --- Helpers ---

function openDashboardModal(uppy: Uppy) {
  const dashboard = uppy.getPlugin('Dashboard');
  if (dashboard && 'openModal' in dashboard) {
    (dashboard as unknown as { openModal: () => void }).openModal();
  }
}

// --- Hook ---

export function useTransloaditUpload({
  onUploadSuccess,
  onAllComplete,
  uploadOptionsPayload = EMPTY_PAYLOAD,
  restrictToSingleFile,
  dashboardNote,
  onModalClosed,
  fetchUploadOptionsFn,
  restrictions: restrictionsOverride,
  inlineTarget,
  dropOnly,
}: TransloaditUploadOptions) {
  const { t, i18n } = useTranslation();
  const uppyRef = useRef<Uppy | null>(null);
  const bucketRef = useRef<string | undefined>(undefined);
  const isOpeningRef = useRef(false);
  const onUploadSuccessRef = useRef(onUploadSuccess);
  const onAllCompleteRef = useRef(onAllComplete);
  const onModalClosedRef = useRef(onModalClosed);
  // Per-account override set in Django admin to allow images larger than 6144x6144.
  // scio1 parity: FileService.upload({ limitResolutionImage: !disableLimitUploadResolutionImage }).
  const disableImageResolutionLimit = useAuthStore(
    (s) => s.company?.feature?.disableLimitUploadResolutionImage ?? false,
  );
  const disableImageResolutionLimitRef = useRef(disableImageResolutionLimit);
  disableImageResolutionLimitRef.current = disableImageResolutionLimit;

  // Keep callback refs in sync without re-creating Uppy
  onUploadSuccessRef.current = onUploadSuccess;
  onAllCompleteRef.current = onAllComplete;
  onModalClosedRef.current = onModalClosed;

  const [fetchUploadOptions] = useLazyQuery(GET_FILE_UPLOAD_OPTIONS, {
    fetchPolicy: 'network-only',
  });

  // Cleanup on unmount — pass reason: 'unmount' so Uppy v3 does NOT cancel
  // in-progress Transloadit assemblies still encoding server-side (#506).
  useEffect(() => {
    return () => {
      uppyRef.current?.close({ reason: 'unmount' });
      uppyRef.current = null;
    };
  }, []);

  /** Fetch upload options from backend (network-only, fresh per upload session).
   *  When fetchUploadOptionsFn is provided (share link context), uses it instead. */
  const getUploadOptions = useCallback(async (): Promise<Record<string, unknown>> => {
    if (fetchUploadOptionsFn) {
      const opts = await fetchUploadOptionsFn();
      if (!opts) throw new Error(t('Failed to get upload options'));
      return opts as Record<string, unknown>;
    }

    const fetchOpts = async (forceTransloadit: boolean): Promise<Record<string, unknown>> => {
      const { data, error } = await fetchUploadOptions({
        variables: {
          payload: forceTransloadit
            ? { ...uploadOptionsPayload, forceTransloadit: true }
            : uploadOptionsPayload,
        },
      });
      if (error) throw new Error(error.message);
      const opts = (data as Record<string, unknown> | undefined)?.getFileUploadOptions as
        | Record<string, unknown>
        | undefined;
      if (!opts) throw new Error(t('Failed to get upload options'));
      return opts;
    };

    // Reuse a warmed prefetch (default payload only) so an inline `open()` has zero wait —
    // see `prefetchUploadOptions`. The Transforge reachability gate below still applies to
    // the cached result, so a warmed default never skips the fallback.
    const cachedDefault = isDefaultPayload(uploadOptionsPayload)
      ? freshCachedOptions(Date.now())
      : null;
    let opts = cachedDefault ? await cachedDefault : await fetchOpts(false);

    // opts.endpoint set → backend picked Transforge. Verify it's reachable from the
    // browser; if not, fall back to the Transloadit shape for this upload session.
    const tfEndpoint = opts.endpoint as string | undefined;
    if (tfEndpoint && !(await isTransforgeReachable(tfEndpoint))) {
      console.warn('[upload] Transforge gateway unreachable — falling back to Transloadit');
      // Not a user-visible failure (the fallback works), but one host failing is that host's
      // network and MANY hosts failing together is the gateway — only Loki can tell them apart.
      reportAppError({
        evt: 'upload_error',
        flow: 'upload-options',
        kind: 'unclassified',
        code: 'transforge_unreachable',
      });
      opts = await fetchOpts(true);
    }

    return opts;
  }, [fetchUploadOptions, fetchUploadOptionsFn, uploadOptionsPayload, t]);

  /** Build a fresh Uppy instance with Dashboard + Transloadit + providers. */
  const buildUppy = useCallback(
    (opts: Record<string, unknown>): Uppy => {
      // Destroy previous instance without cancelling in-flight Transloadit assemblies.
      // close({ reason: 'unmount' }) uninstalls plugins + cleans up DOM/listeners,
      // but Transloadit plugin skips assembly cancellation when reason !== 'user' (#506).
      if (uppyRef.current) {
        uppyRef.current.close({ reason: 'unmount' });
        uppyRef.current = null;
      }

      const restrictions: Restrictions = {
        ...(opts.restrictions as Restrictions),
        ...(restrictToSingleFile && { maxNumberOfFiles: 1 }),
        ...restrictionsOverride,
      };

      const uppy = new Uppy({
        restrictions,
        allowMultipleUploads: false,
        onBeforeUpload: (files) => {
          // Inject fileID into meta for each file (scio1/scio2 pattern)
          const updated: typeof files = {};
          for (const [fileID, file] of Object.entries(files)) {
            // Sync the (possibly user-edited via metaFields) meta.name onto the upload's
            // name: server-side type detection AND the completion pingback both derive
            // from the upload name's extension, so the edited name must be synced onto
            // the upload and must keep an extension (#1786 + user-testing regression).
            // A rename with no dot at all inherits the original file's extension; a
            // rename containing a dot is trusted as an explicit user extension. The
            // normalized value goes to BOTH name and meta.name so the Uppy display, the
            // assembly fields.name, and the saved originalFileName all agree.
            const editedName = (file.meta?.name as string) || file.name;
            const name =
              !editedName.includes('.') && file.name.includes('.')
                ? `${editedName}${file.name.slice(file.name.lastIndexOf('.'))}`
                : editedName;
            updated[fileID] = {
              ...file,
              name,
              meta: { ...file.meta, fileID, name },
            };
          }
          return updated;
        },
      });

      // Set locale after construction (scio2 pattern)
      const locale = UPPY_LOCALE_MAP[i18n.language] || en_US;
      uppy.setOptions({ locale });

      // Dashboard — modal mode by default; inline mode when `inlineTarget` is provided.
      const isDark = document.documentElement.classList.contains('dark');
      uppy.use(Dashboard, {
        inline: Boolean(inlineTarget),
        target: inlineTarget ?? 'body',
        proudlyDisplayPoweredByUppy: false,
        theme: isDark ? 'dark' : 'light',
        // dropOnly → no note (fully bare box); otherwise custom note or the default.
        note: dropOnly ? undefined : dashboardNote || t('Images, Documents, Audio and Video only'),
        metaFields: [{ id: 'name', name: t('File name'), placeholder: t('Enter file name') }],
      });

      // Transforge migration (USE_TRANSFORGE on the backend): when enabled, the
      // backend (open-api getUploadOptions) injects `endpoint` (Transforge gateway)
      // and `companion_url` (our self-hosted Companion) into opts. When absent, these
      // stay undefined and Uppy falls back to Transloadit SaaS + Transloadit Companion.
      // `service` is a plugin-level option (NOT part of assemblyOptions).
      const tfService = (opts.endpoint as string | undefined) || undefined;
      const tfCompanionUrl = (opts.companion_url as string | undefined) || COMPANION_URL;
      const tfCompanionAllowedHosts =
        (opts.companion_allowed_hosts as string | undefined) || COMPANION_ALLOWED_HOSTS;

      // Transloadit — 1 assembly per file.
      // In Uppy v3, assemblyOptions is called per-file. The backend returns
      // fields: ["name", "fileID"] as an array. Uppy resolves each field name
      // from file.meta, producing unique values per file (dedupe creates separate assemblies).
      uppy.use(Transloadit, {
        // Transforge gateway when the backend sets opts.endpoint; else Transloadit SaaS.
        ...(tfService ? { service: tfService } : {}),
        assemblyOptions: (_file: UppyFile): AssemblyOptions => {
          // Backend returns fields as an array of meta field names, e.g. ["name", "fileID"].
          // Uppy's Transloadit plugin resolves arrays by extracting values from file.meta
          // per file (see getFields in AssemblyOptions.js). This produces unique field values
          // per file (different name, different fileID), ensuring 1 assembly per file.
          // onBeforeUpload already injects fileID into file.meta for this to work.
          // IMPORTANT: Do NOT spread the array into an object — that produces { "0": "name", "1": "fileID" }.
          return structuredClone(opts) as AssemblyOptions;
        },
        waitForEncoding: false,
        waitForMetadata: false,
        // NOT just upload concurrency. @uppy/transloadit 3.x shares ONE RateLimitedQueue(limit)
        // between assembly status polling (Assembly.js, priority 0) and Companion token requests
        // (@uppy/tus, priority -1). Polling only stops once the assembly status socket connects
        // (Assembly.js: `if (!this.socket || !this.socket.connected) fetchStatus()`), and on the
        // Transforge gateway it never does — measured 6.5 poll req/s sustained for 8 min, which
        // pins both slots and starves the priority -1 token requests: 49 Drive files sat at 0%
        // for 7m40s, then drained in 73s. Need limit/0.3s > N/2s → >7.5 at N=50; 20 is the
        // plugin default and survives poll latency doubling. Do not lower it to throttle
        // uploads — that throttles polling too, and polling outranks uploads.
        //
        // Transforge only. Transloadit SaaS keeps 2: its polling is not known to run unbounded,
        // it is the path most accounts still use, and we have no measurement there to justify
        // 10x-ing their upload concurrency. Raised 2->5 globally once (2d018c04e) and reverted
        // (d5a1f9a5); 5 was under the bar anyway.
        limit: tfService ? 20 : 2,
      });

      // Cloud providers (from server opts.providers). `dropOnly` suppresses them so the
      // inline manifest file-upload field shows a bare drag-drop box (legacy Canva parity).
      const providers = dropOnly ? [] : (opts.providers as string[]) || [];

      if (providers.includes('webcam')) {
        uppy.use(Webcam, { target: Dashboard });
      }
      if (providers.includes('google-drive')) {
        uppy.use(GoogleDrive, {
          target: Dashboard,
          companionUrl: tfCompanionUrl,
          companionAllowedHosts: tfCompanionAllowedHosts,
        });
      }
      if (providers.includes('dropbox')) {
        uppy.use(Dropbox, {
          target: Dashboard,
          companionUrl: tfCompanionUrl,
          companionAllowedHosts: tfCompanionAllowedHosts,
        });
      }
      if (providers.includes('onedrive')) {
        uppy.use(OneDrive, {
          target: Dashboard,
          companionUrl: tfCompanionUrl,
          companionAllowedHosts: tfCompanionAllowedHosts,
        });
      }

      // --- Events (follows scio2 _openUploadModal pattern) ---

      // Client-side validation on file add
      uppy.on('file-added', async (file: UppyFile) => {
        const blob = file.data as File;

        // Empty file: the gateway only finds out AFTER the upload completes and then fails the
        // assembly with INPUT_FILE_EMPTY (400), so the user sees a raw server error and a dead
        // asset instead of a reason. Reject it here, before an assembly is created.
        // `file.size` (not `blob.size`) — for cloud-provider picks `file.data` is a stub and the
        // size comes from the provider. Strict `=== 0` on purpose: providers report NO size for
        // Google native files (Docs/Sheets/Slides), and null/undefined must not count as empty.
        if (file.size === 0) {
          toast.error(t('{{name}} is empty (0 bytes) and was skipped.', { name: file.name }));
          uppy.removeFile(file.id);
          return;
        }

        if (!disableImageResolutionLimitRef.current) {
          const resolutionError = await validateImageResolution(blob);
          if (resolutionError) {
            toast.error(resolutionError.message);
            uppy.removeFile(file.id);
            return;
          }
        }

        const bitrateError = await validateVideoBitrate(blob);
        if (bitrateError) {
          toast.error(bitrateError.message);
          uppy.removeFile(file.id);
          return;
        }
      });

      // Per-file failure — the only place the transport / assembly error OBJECT is visible
      // (`complete.failed` below only carries file names, so the toast stays there and the
      // telemetry lives here). The provider's assembly error code rides along as `code`.
      // The user's own file (too large, unsupported, …) is not reported: the toast covers
      // them and the upload-processor lane holds the denominator.
      uppy.on('upload-error', (_file, error) => {
        if (isUserFileUploadError(error)) return;
        reportAppError({
          evt: 'upload_error',
          flow: 'upload-assembly',
          ...classifyUploadError('assembly', error),
          error,
        });
      });

      // Per-file success — extract assembly ID and call caller's callback.
      // scio2: uppy.on('upload-success', (upload) => { _processUploads([upload], ...) })
      uppy.on('upload-success', (file: UppyFile | undefined) => {
        if (!file) return;
        const fileWithTransloadit = file as UppyFile & {
          transloadit?: { assembly?: string };
        };
        const assemblyId = fileWithTransloadit.transloadit?.assembly || '';

        // Use meta.name (edited by user via metaFields) over original file.name
        const name = (file.meta?.name as string) || file.name || '';
        const extension = name.includes('.') ? name.split('.').pop() || '' : '';

        onUploadSuccessRef.current(
          {
            name,
            type: file.type || '',
            size: file.size || 0,
            extension,
          },
          assemblyId,
          bucketRef.current,
        );
      });

      // All files complete — handle failures, close modal (scio2 pattern).
      // Do NOT call uppy.close()/destroy() — it triggers cancelAll() which cancels
      // Transloadit assemblies still processing server-side.
      // Instance is destroyed on next open() call in buildUppy().
      uppy.on('complete', (result) => {
        const { failed } = result;

        if (failed && failed.length > 0) {
          const names = failed.map((file) => (file as UppyFile).name).join(', ');
          toast.error(t('Failed to upload {{names}}.', { names }));
        }

        onAllCompleteRef.current?.();

        // Close modal (scio2: modal.closeModal()) — modal-only plumbing, skipped inline.
        if (!inlineTarget) {
          const dashboard = uppy.getPlugin('Dashboard');
          if (dashboard && 'closeModal' in dashboard) {
            (dashboard as unknown as { closeModal: () => void }).closeModal();
          }
        }
      });

      // Dashboard closed without completing upload — notify caller
      uppy.on('dashboard:modal-closed' as 'complete', () => {
        onModalClosedRef.current?.();
      });

      uppyRef.current = uppy;
      return uppy;
    },
    [
      i18n.language,
      t,
      restrictToSingleFile,
      dashboardNote,
      restrictionsOverride,
      inlineTarget,
      dropOnly,
    ],
  );

  /** Build a fresh Uppy instance each time (ensures clean state per session). */
  const buildFreshUppy = useCallback(async (): Promise<Uppy> => {
    // Don't close() old instance — it cancels in-flight Transloadit assemblies (#506).
    // buildUppy() handles detaching the previous modal UI.

    const opts = await getUploadOptions();
    bucketRef.current = (opts.bucket as string) || undefined;
    return buildUppy(opts);
  }, [getUploadOptions, buildUppy]);

  /** Open the Uppy Dashboard modal. Builds a fresh Uppy instance each time. */
  const open = useCallback(async () => {
    if (isOpeningRef.current) return;
    isOpeningRef.current = true;
    // Inline mode surfaces its own in-place loader (see FileUploadField) — the full-screen
    // overlay only fits a modal open triggered by an explicit click. Inline `open()` fires
    // automatically on field mount, so the overlay would flash over the whole dialog.
    const dismissLoading = inlineTarget ? () => {} : showLoadingOverlay(300);
    try {
      const uppy = await buildFreshUppy();
      dismissLoading();
      // Inline mode has no modal to open — the Dashboard is already mounted into inlineTarget.
      if (!inlineTarget) {
        openDashboardModal(uppy);
      }
    } catch (err) {
      dismissLoading();
      if (!isUploadCancelled(err))
        reportAppError({
          evt: 'upload_error',
          flow: 'upload-options',
          ...classifyUploadError('options', err),
          error: err,
        });
      const message = err instanceof Error ? err.message : t('Failed to open Upload Modal.');
      toast.error(message);
    } finally {
      isOpeningRef.current = false;
    }
  }, [buildFreshUppy, t, inlineTarget]);

  /** Add files to Uppy (e.g. from drag-drop) then open the modal. */
  const openWithFiles = useCallback(
    async (files: File[]) => {
      if (isOpeningRef.current) return;
      isOpeningRef.current = true;
      // Inline mode has its own in-place loader — skip the full-screen overlay (see `open`).
      const dismissLoading = inlineTarget ? () => {} : showLoadingOverlay(300);
      try {
        const uppy = await buildFreshUppy();

        for (const file of files) {
          try {
            uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              source: 'Local',
              isRemote: false,
            });
          } catch {
            // Uppy throws if file doesn't match restrictions — handled by Uppy informer
          }
        }

        dismissLoading();
        if (!inlineTarget) {
          openDashboardModal(uppy);
        }
      } catch (err) {
        dismissLoading();
        if (!isUploadCancelled(err))
          reportAppError({
            evt: 'upload_error',
            flow: 'upload-options',
            ...classifyUploadError('options', err),
            error: err,
          });
        const message = err instanceof Error ? err.message : t('Failed to open Upload Modal.');
        toast.error(message);
      } finally {
        isOpeningRef.current = false;
      }
    },
    [buildFreshUppy, t, inlineTarget],
  );

  return { open, openWithFiles };
}
